import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getParticleCountSegmentThreshold, getStatus } from "./thresholdUtils";
import { getBoolStatus, getWaterStatus, METRIC_DEFINITIONS } from "./metricDefinitions";

const PAGE = { width: 279.4, height: 215.9, margin: 12 };
const CONTENT_WIDTH = PAGE.width - PAGE.margin * 2;

const C = {
  black: [18, 18, 18],
  white: [255, 255, 255],
  gray: [126, 123, 124],
  lightGray: [218, 216, 216],
  paleGray: [242, 242, 242],
  green: [0, 176, 80],
  yellow: [255, 255, 0],
  red: [255, 0, 0],
  link: [0, 102, 204],
};

const RESULT_COLUMNS = [52, 36, 36, 36, 36, 59.4];
const STATUS_RANK = { na: -1, normal: 0, warn: 1, critical: 2 };

const OIL_ROWS = [
  { key: "viscosity_100c", label: "Viscosity @ 100C:", threshKey: "VISCOSITY_100", precision: 2, legend: "cSt Flow Measurement" },
  { key: "oxidation", label: "Oxidation Value:", threshKey: "OXIDATION", precision: 2, legend: "Oil Life" },
  { key: "fuel", label: "Fuel Dilution:", threshKey: "FUEL_DILUTION", precision: 2, suffix: "%", legend: "Contamination" },
  { key: "soot", label: "Soot:", threshKey: "SOOT", precision: 2, suffix: "%", legend: "Contamination" },
  { key: "particle_count", label: "Particle Count:", legend: "Contamination", particle: true },
  { key: "water", label: "Water:", precision: 3, legend: "Contamination" },
  { key: "uglyc", label: "Glycol:", legend: "Contamination", boolean: true },
  { key: "potassium", label: "Potassium:", threshKey: "POTASSIUM", precision: 0, legend: "Contamination / Coolant" },
  { key: "silicon", label: "Silicon:", threshKey: "SILICON", precision: 0, legend: "Anti-Foam, Dirt" },
];

const ADDITIVE_ROWS = [
  { key: "calcium", label: "Calcium", precision: 0, legend: "Detergent" },
  { key: "sodium", label: "Sodium", threshKey: "SODIUM", precision: 0, legend: "Detergent" },
  { key: "magnesium", label: "Magnesium", precision: 0, legend: "Detergent" },
  { key: "phosphorous", label: "Phosphorus", threshKey: "PHOSPHOROUS", precision: 0, legend: "Anti-Wear" },
  { key: "zinc", label: "Zinc", precision: 0, legend: "Anti-Wear" },
  { key: "molybdenum", label: "Molybdenum", precision: 0, legend: "Friction Reducer" },
  { key: "boron", label: "Boron", precision: 0, legend: "Dispersant / Friction Reducer" },
];

const WEAR_ROWS = [
  { key: "iron", label: "Iron", threshKey: "IRON", precision: 0, legend: "Valvetrain, Cylinder Bore Wear" },
  { key: "chromium", label: "Chromium", threshKey: "CHROMIUM", precision: 0, legend: "Piston Ring Wear" },
  { key: "copper", label: "Copper", threshKey: "COPPER", precision: 0, legend: "Bushing, Bearing Wear" },
  { key: "tin", label: "Tin", threshKey: "TIN", precision: 0, legend: "Moly Additive / Bearing Wear" },
  { key: "lead", label: "Lead", threshKey: "LEAD", precision: 0, legend: "Bearing Wear" },
  { key: "aluminum", label: "Aluminum", threshKey: "ALUMINUM", precision: 0, legend: "Piston, Aluminum Bore Wear" },
  { key: "manganese", label: "Manganese", threshKey: "MANGANESE", precision: 0, legend: "Valve Guide Wear / Octane Booster" },
  { key: "nickel", label: "Nickel", threshKey: "NICKEL", precision: 0, legend: "Nickel Alloy Wear" },
  { key: "titanium", label: "Titanium", threshKey: "TITANIUM", precision: 0, legend: "Wrist Pin, Retainer Wear" },
  { key: "total_metals", label: "Total Metals:", threshKey: "TOTAL_METALS", precision: 0, legend: "Total Wear Metals", bold: true },
  { key: "wear_per_thousand", label: "Wear / 1,000 Miles:", threshKey: "WEAR_PER_THOUSAND", precision: 1, legend: "Wear Metals / 1,000 Miles", bold: true },
];

const ACTION_ROWS = [
  ["Viscosity @ 100C:", "Verify that the submitted viscosity matches the oil installed. Low viscosity commonly points to fuel dilution; high viscosity commonly points to oxidation. Check fuel delivery, air/fuel ratio, and the oil drain interval."],
  ["Oxidation Value:", "A high oxidation result can mean the oil is past its useful life or contains ester base oils. If the oil is not ester based, shorten the drain interval; otherwise establish a fresh-oil baseline."],
  ["Fuel Dilution:", "Check injectors or carburetors. Dirty injectors or a gummed carburetor can cause fuel dilution problems."],
  ["Particle Count:", "Fresh oil and a new oil filter are the first steps. If the count remains high, investigate abnormal wear or contamination."],
  ["Water:", "Check the cooling system for leaks and review storage practices. Avoid short starts that do not bring the equipment fully to operating temperature."],
  ["Glycol:", "Glycol indicates antifreeze contamination. Check the cooling system for leaks immediately."],
  ["Potassium:", "Check for cooling-system leaks and accidental cross-contamination from funnels or other service equipment."],
  ["Silicon:", "Check the air filter and breathers. New seals and sealants can temporarily raise silicon; continued sampling will distinguish sealant from dirt contamination."],
];

const WEAR_ACTION_ROWS = [
  ["Wear Metals (ppm):", "Any red Warning-level wear result should be taken seriously. Investigate promptly."],
  ["Iron", "Typically indicates cylinder-bore or valvetrain wear. Check leak-down and valve lash."],
  ["Chromium", "Typically indicates piston-ring wear. Check leak-down; chromium can also come from steel alloys."],
  ["Copper", "Typically indicates bearing, bushing, or distributor-gear wear. Check the filter for debris."],
  ["Tin", "Typically indicates bearing wear. High copper, tin, and lead together can indicate bearing damage."],
  ["Lead", "Typically indicates bearing wear. If lead alone is high, confirm whether leaded fuel or aviation gasoline was used."],
  ["Aluminum", "Typically indicates piston or aluminum-bore wear. High tin and aluminum together can indicate bi-metal bearing wear."],
  ["Manganese", "May indicate use of an MMT-based octane booster."],
  ["Titanium", "May indicate retainer or other titanium-component wear, or a titanium oil additive. Compare with fresh oil."],
  ["Total Metals:", "A red Warning result with elevated individual metals indicates greater severity. Inspect before continued operation."],
  ["Wear / 1,000 miles:", "Confirm the sampling procedure and recorded miles or hours. An elevated wear rate warrants prompt trend follow-up."],
];

const RESOURCES = [
  ["Viscosity Grades Explained:", "https://youtu.be/48pviPLgaPQ"],
  ["Viscosity Loss Explained:", "https://youtu.be/2-ECI5uK9eE"],
  ["How Motor Oil Is Made:", "https://youtu.be/57ubgGVkCF8"],
  ["Motor Oil Additives:", "https://youtu.be/CAGT5inQScE"],
  ["Diesel Fuel Additives:", "https://youtu.be/2m4BBqIAUkY"],
  ["Gasoline Fuel Additives:", "https://youtu.be/MAAJCEatYsY"],
  ["Premium Gasoline:", "https://youtu.be/DAmEGI7xvBw"],
  ["Motor Oil Dyno Testing:", "https://youtu.be/e8dCFcF0qBQ"],
  ["Motor Oil Wear Testing:", "https://youtu.be/ipunDlG1k-8"],
];

function clean(value) {
  return String(value ?? "").trim();
}

function asNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function compactDate(value) {
  if (!value) return "-";
  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) return `${Number(dateOnly[2])}/${Number(dateOnly[3])}/${dateOnly[1].slice(-2)}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return clean(value);
  return `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(-2)}`;
}

function integerWithCommas(value) {
  const number = asNumber(value);
  return number === null ? "-" : Math.round(number).toLocaleString("en-US");
}

function formatValue(value, row) {
  if (row.particle) {
    const values = [value?.particle_count_0, value?.particle_count_1, value?.particle_count_2];
    return values.every((part) => part === null || part === undefined || part === "")
      ? "-"
      : values.map((part) => asNumber(part) ?? 0).join("/");
  }
  if (value === null || value === undefined || value === "") return "-";
  if (row.boolean) {
    const normalized = clean(value).toUpperCase();
    if (normalized === "N" || normalized.startsWith("NEG")) return "Negative";
    if (normalized === "P" || normalized.startsWith("POS")) return "Positive";
  }
  const number = asNumber(value);
  if (number !== null && row.precision !== undefined) {
    const formatted = row.precision === 0 ? String(Math.round(number)) : number.toFixed(row.precision);
    return `${formatted}${row.suffix || ""}`;
  }
  return clean(value);
}

function rowValue(sample, row) {
  return row.particle ? sample : sample?.[row.key];
}

function worstStatus(statuses) {
  return statuses.reduce((worst, status) => STATUS_RANK[status] > STATUS_RANK[worst] ? status : worst, "na");
}

function statusFor(sample, row, template) {
  if (!sample) return "na";
  if (row.particle) {
    const base = template?.PARTICLE_COUNT;
    return worstStatus([0, 1, 2].map((index) => {
      const value = asNumber(sample[`particle_count_${index}`]);
      return value === null ? "na" : getStatus(value, getParticleCountSegmentThreshold(base, index));
    }));
  }
  if (row.key === "water") return getWaterStatus(sample[row.key]);
  if (row.boolean) return getBoolStatus(sample[row.key]);
  const value = asNumber(sample[row.key]);
  const threshold = row.threshKey ? template?.[row.threshKey] : null;
  return value === null || !threshold ? "na" : getStatus(value, threshold);
}

function statusFill(status) {
  if (status === "critical") return C.red;
  if (status === "warn") return C.yellow;
  if (status === "normal") return [223, 245, 229];
  return C.white;
}

function statusText(status) {
  return status === "critical" ? "Warning" : status === "warn" ? "Caution" : status === "normal" ? "Good" : "Not Rated";
}

function prepareSamples(current, history = []) {
  const id = clean(current?.lab_sample_id);
  const previous = (history || [])
    .filter((sample) => sample && clean(sample.lab_sample_id) !== id)
    .sort((a, b) => new Date(b.sample_tested_date || 0) - new Date(a.sample_tested_date || 0));
  return [current, ...previous].slice(0, 4);
}

function drawLegend(doc, action = false) {
  const x = 148;
  const y = 7;
  const width = 119;
  const labelWidth = 92;
  const rows = action
    ? [["Check sampling method and re-sample immediately.", "Warning", C.red], ["Re-sample at normal drain interval; check trends.", "Caution", C.yellow], ["No action required; results are normal.", "Good", C.green]]
    : [["Results outside the normal range", "", C.red], ["Results with borderline values", "", C.yellow], ["Results within the normal range", "", C.green]];
  doc.setFillColor(...C.gray);
  doc.setDrawColor(...C.black);
  doc.rect(x, y, width, 5, "FD");
  doc.setTextColor(...C.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.5);
  doc.text(action ? "Results Color Codes - Action Steps" : "Results Color Codes", x + width / 2, y + 3.5, { align: "center" });
  rows.forEach(([label, result, color], index) => {
    const rowY = y + 5 + index * 5;
    doc.setFillColor(...C.white);
    doc.rect(x, rowY, labelWidth, 5, "FD");
    doc.setTextColor(...C.black);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.7);
    doc.text(label, x + labelWidth / 2, rowY + 3.5, { align: "center" });
    doc.setFillColor(...color);
    doc.rect(x + labelWidth, rowY, width - labelWidth, 5, "FD");
    if (result) {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...(color === C.yellow ? C.black : C.white));
      doc.text(result, x + labelWidth + (width - labelWidth) / 2, rowY + 3.5, { align: "center" });
    }
  });
}

function drawBrandHeader(doc, logo, title, actionLegend = false) {
  if (logo) {
    try { doc.addImage(logo, "PNG", PAGE.margin, 8, 112, 18); } catch (_) { /* text fallback below */ }
  }
  if (!logo) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(25);
    doc.setTextColor(0, 151, 76);
    doc.text("SPEEDiagnostix", PAGE.margin, 22);
  }
  drawLegend(doc, actionLegend);
  doc.setFillColor(...C.gray);
  doc.setDrawColor(...C.black);
  doc.rect(PAGE.margin, 27, CONTENT_WIDTH, 7, "FD");
  doc.setTextColor(...C.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.text(title, PAGE.width / 2, 32, { align: "center" });
  return 34;
}

function headerRow(label, columns = 6) {
  return [{ content: label, colSpan: columns, styles: { fillColor: C.gray, textColor: C.white, fontStyle: "bold", halign: "left" } }];
}

function reportMetaRows(samples) {
  const cells = (label, getter) => [label, ...Array.from({ length: 4 }, (_, index) => getter(samples[index])), ""];
  return [
    [{ content: "Sample Information", styles: { fillColor: C.gray, textColor: C.white, fontStyle: "bold", halign: "center" } }, { content: "Current Sample", styles: { fillColor: C.lightGray, fontStyle: "bold", halign: "center" } }, { content: "Previous Samples", colSpan: 3, styles: { fillColor: C.lightGray, fontStyle: "bold", halign: "center" } }, { content: "", styles: { fillColor: C.lightGray } }],
    cells("Sample ID #:", (sample) => clean(sample?.lab_sample_id) || "-"),
    cells("Sample Date:", (sample) => compactDate(sample?.sample_tested_date)),
    cells("Oil Brand:", (sample) => clean(sample?.oil_brand) || "-"),
    cells("Viscosity Grade:", (sample) => clean(sample?.oil_viscosity) || "-"),
    cells("Odometer:", (sample) => integerWithCommas(sample?.engine_miles)),
    cells("Sample Mileage:", (sample) => integerWithCommas(sample?.sample_miles)),
  ];
}

function resultRows(samples, rows, template, subsection = null) {
  const body = [];
  if (subsection) body.push([{ content: subsection, colSpan: 6, styles: { fontStyle: "bold", fillColor: C.paleGray } }]);
  rows.forEach((row) => {
    body.push([
      { content: row.label, styles: { fontStyle: row.bold ? "bold" : "normal", halign: "right" } },
      ...Array.from({ length: 4 }, (_, index) => {
        const sample = samples[index];
        const status = statusFor(sample, row, sample?._pdfThresholds || template);
        return { content: sample ? formatValue(rowValue(sample, row), row) : "-", styles: { fillColor: statusFill(status), fontStyle: row.bold ? "bold" : "normal", halign: "center" } };
      }),
      { content: row.legend, styles: { fillColor: C.lightGray, halign: "center" } },
    ]);
  });
  return body;
}

function drawFirstPage(doc, sample, template, history, assets) {
  drawBrandHeader(doc, assets.logo, "Oil Analysis Report");
  const samples = prepareSamples(sample, history);
  const allStatuses = [...OIL_ROWS, ...WEAR_ROWS].map((row) => statusFor(sample, row, template));
  const condition = worstStatus(allStatuses);
  const infoY = 34;
  doc.setFontSize(6.2);
  doc.setTextColor(...C.black);
  doc.setFont("helvetica", "bold");
  doc.text("Name:", 62, infoY + 3.8, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.text(clean(sample.customer_name) || "-", 90, infoY + 3.8, { align: "center" });
  doc.setFont("helvetica", "bold");
  doc.text("Sample Type:", 168, infoY + 3.8, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.text(clean(sample.component_type) || "-", 210, infoY + 3.8, { align: "center" });
  doc.setFont("helvetica", "bold");
  doc.text("Unit ID:", 62, infoY + 8.2, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.text(clean(sample.vehicle_name) || [sample.vehicle_year, sample.vehicle_make, sample.vehicle_model].filter(Boolean).join(" ") || "-", 90, infoY + 8.2, { align: "center" });
  doc.setFont("helvetica", "bold");
  doc.text("Condition:", 168, infoY + 8.2, { align: "right" });
  doc.setFillColor(...(condition === "normal" ? C.green : statusFill(condition)));
  doc.rect(169, infoY + 4.5, 98, 4.5, "F");
  doc.setTextColor(...(condition === "normal" || condition === "critical" ? C.white : C.black));
  doc.text(statusText(condition), 218, infoY + 7.8, { align: "center" });

  const tableBody = [
    ...reportMetaRows(samples),
    [{ content: "Oil Health", styles: { fillColor: C.gray, textColor: C.white, fontStyle: "bold", halign: "center" } }, { content: "Test Results", styles: { fillColor: C.gray, textColor: C.white, fontStyle: "bold", halign: "center" } }, { content: "Previous Sample Results", colSpan: 3, styles: { fillColor: C.lightGray, fontStyle: "bold", halign: "center" } }, { content: "Legend", styles: { fillColor: C.gray, textColor: C.white, fontStyle: "bold", halign: "center" } }],
    ...resultRows(samples, OIL_ROWS, template),
    ...resultRows(samples, ADDITIVE_ROWS, template, "Additives (ppm):"),
    [{ content: "Equipment Health", styles: { fillColor: C.gray, textColor: C.white, fontStyle: "bold", halign: "center" } }, { content: "Test Results", styles: { fillColor: C.gray, textColor: C.white, fontStyle: "bold", halign: "center" } }, { content: "Previous Sample Results", colSpan: 3, styles: { fillColor: C.lightGray, fontStyle: "bold", halign: "center" } }, { content: "Legend", styles: { fillColor: C.gray, textColor: C.white, fontStyle: "bold", halign: "center" } }],
    ...resultRows(samples, WEAR_ROWS, template, "Wear Trend: Wear Metals (ppm):"),
    headerRow("Comments / Recommendations"),
    [{ content: clean(sample.oilGeekNote) || "No comments or recommendations are available for this report.", colSpan: 6, styles: { minCellHeight: 9, valign: "top", halign: "left" } }],
  ];

  autoTable(doc, {
    body: tableBody,
    startY: 43.5,
    margin: { left: PAGE.margin, right: PAGE.margin, bottom: 11 },
    theme: "grid",
    tableLineColor: C.black,
    tableLineWidth: 0.18,
    styles: { font: "helvetica", fontSize: 5.45, textColor: C.black, cellPadding: { top: 0.62, right: 0.7, bottom: 0.62, left: 0.7 }, overflow: "linebreak", valign: "middle" },
    columnStyles: Object.fromEntries(RESULT_COLUMNS.map((width, index) => [index, { cellWidth: width }])),
    didParseCell(data) {
      if (data.column.index === 0 && data.row.index > 0) data.cell.styles.fontStyle = data.cell.styles.fontStyle || "bold";
    },
  });
}

function descriptionRows() {
  const rows = [
    ["Viscosity @ 100C:", METRIC_DEFINITIONS.viscosity_100c],
    ["Oxidation Value:", METRIC_DEFINITIONS.oxidation],
    ["Fuel Dilution:", METRIC_DEFINITIONS.fuel],
    ["Soot:", METRIC_DEFINITIONS.soot],
    ["Water:", METRIC_DEFINITIONS.water],
    ["Glycol:", METRIC_DEFINITIONS.uglyc],
    ["Potassium:", METRIC_DEFINITIONS.potassium],
    ["Silicon:", METRIC_DEFINITIONS.silicon],
    ["Additives (ppm):", "Different oils contain different additive packages, so individual additive types and levels vary by application."],
    ...ADDITIVE_ROWS.map((row) => [row.label, METRIC_DEFINITIONS[row.key] || row.legend]),
  ];
  return rows;
}

function drawExplanationPage(doc, sample, assets) {
  doc.addPage();
  drawBrandHeader(doc, assets.logo, "Oil Analysis Report - Explained");
  const name = clean(sample.customer_name) || "The name of the customer";
  const unit = clean(sample.vehicle_name) || "The ID of the equipment sampled";
  const sampleType = clean(sample.component_type) || "The type of equipment sampled";
  autoTable(doc, {
    body: [
      ["Name:", name, "Sample Type:", sampleType],
      ["Unit ID:", unit, "Condition:", "The overall condition of the sample"],
    ],
    startY: 34,
    margin: { left: PAGE.margin, right: PAGE.margin },
    theme: "plain",
    styles: { fontSize: 6.2, cellPadding: 0.7 },
    columnStyles: { 0: { cellWidth: 52, halign: "right", fontStyle: "bold" }, 1: { cellWidth: 76 }, 2: { cellWidth: 45, halign: "right", fontStyle: "bold" }, 3: { cellWidth: 82.4 } },
  });
  const rows = [
    headerRow("Sample Information", 2),
    ["Sample ID #:", "The unique sample-bottle ID provides traceability for each SPEEDiagnostix sample."],
    ["Sample Date:", compactDate(sample.sample_tested_date)],
    ["Oil Brand:", "The installed oil brand supports comparison with fresh-oil specifications when available."],
    ["Viscosity Grade:", "The lab viscosity is compared with the expected new-oil grade to evaluate oil health."],
    ["Mileage:", "Miles on the oil are essential to calculating and evaluating wear rate."],
    headerRow("Oil Health                                      Test Descriptions", 2),
    ...descriptionRows(),
    headerRow("Equipment Health                              Test Descriptions", 2),
    ["Wear Trend / Wear Metals (ppm):", "Parts per million of metals from worn components. Consistent sampling makes the trend more useful than any isolated reading."],
    ...WEAR_ROWS.map((row) => [row.label, METRIC_DEFINITIONS[row.key] || row.legend]),
    headerRow("Comments / Recommendations", 2),
    ["Wear Trend:", "Statistical review of changes in wear rates over the history of samples from this equipment. Regular samples help identify changes before values reach a caution or warning level."],
  ];
  autoTable(doc, {
    body: rows,
    startY: doc.lastAutoTable.finalY,
    margin: { left: PAGE.margin, right: PAGE.margin, bottom: 11 },
    theme: "grid",
    tableLineColor: C.black,
    tableLineWidth: 0.18,
    styles: { fontSize: 4.85, cellPadding: { top: 0.42, right: 0.7, bottom: 0.42, left: 0.7 }, textColor: C.black, valign: "middle" },
    columnStyles: { 0: { cellWidth: 53, fontStyle: "bold", halign: "right" }, 1: { cellWidth: 202.4 } },
  });
}

function drawStepsPage(doc, assets) {
  doc.addPage();
  drawBrandHeader(doc, assets.logo, "Oil Analysis Report - Steps To Take", true);
  const intro = "If a report returns a yellow Caution or red Warning condition, do not panic. First verify the submitted information and the sampling method. An improperly taken sample can create a misleading result. A Caution result calls for re-sampling at the normal drain interval and checking the trend. A Warning result is beyond the acceptable range: follow the applicable recommendation below and take another sample as soon as practical. Two consecutive Warning samples are grounds for removing the equipment from service until the cause is understood.";
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.2);
  doc.setTextColor(...C.black);
  const introLines = doc.splitTextToSize(intro, CONTENT_WIDTH - 2);
  doc.text(introLines, PAGE.margin + 1, 39, { lineHeightFactor: 1.25 });
  const startY = 39 + introLines.length * 2.85 + 2;
  const rows = [
    headerRow("Oil Health                         Recommended Action", 2),
    ...ACTION_ROWS,
    headerRow("Equipment Health                  Recommended Action", 2),
    ...WEAR_ACTION_ROWS,
    headerRow("Wear Trend", 2),
    ["", "A red Warning result indicates a significant change compared with prior samples. Re-sample promptly to determine whether it is an anomaly or a developing equipment-health problem."],
  ];
  autoTable(doc, {
    body: rows,
    startY,
    margin: { left: PAGE.margin, right: PAGE.margin, bottom: 11 },
    theme: "grid",
    tableLineColor: C.black,
    tableLineWidth: 0.18,
    styles: { fontSize: 5.7, cellPadding: { top: 0.82, right: 0.8, bottom: 0.82, left: 0.8 }, textColor: C.black, valign: "middle" },
    columnStyles: { 0: { cellWidth: 34, fontStyle: "bold", halign: "right" }, 1: { cellWidth: 221.4 } },
  });
}

function drawResourcesPage(doc, assets) {
  doc.addPage();
  doc.setFillColor(...C.gray);
  doc.setDrawColor(...C.black);
  doc.rect(PAGE.margin, 18, CONTENT_WIDTH, 7, "FD");
  doc.setTextColor(...C.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.text("Helpful Resources", PAGE.width / 2, 23, { align: "center" });
  doc.setTextColor(...C.black);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.2);
  doc.text("For additional context and guidance, these videos address common oil-analysis questions and issues.", PAGE.width / 2, 30, { align: "center" });
  doc.setFillColor(...C.gray);
  doc.rect(PAGE.margin, 34, CONTENT_WIDTH, 5, "F");
  let y = 47;
  RESOURCES.forEach(([label, url]) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...C.black);
    doc.text(label, 74, y, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C.link);
    doc.textWithLink(url, 108, y, { url });
    y += 8;
  });
  doc.setDrawColor(...C.black);
  doc.rect(PAGE.margin, 39, 167, 78);
  doc.rect(PAGE.margin + 167, 39, CONTENT_WIDTH - 167, 78);
  if (assets.oilGeek) {
    try { doc.addImage(assets.oilGeek, "PNG", 205, 47, 48, 45.6); } catch (_) { /* optional image */ }
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...C.black);
    doc.text("The Motor Oil Geek", 223, 76, { align: "center" });
  }
  doc.setFillColor(...C.gray);
  doc.rect(PAGE.margin, 117, CONTENT_WIDTH, 5, "F");
  const steps = [
    "Step 1 - Use the OEM-recommended oil specification and viscosity. During break-in, complete two early oil changes: 500 to 1,000 miles and again at 3,000 to 4,000 miles. If the engine is already broken in, begin with Step 3.",
    "Step 2 - Take used-oil samples at each oil change to establish a reliable trend.",
    "Step 3 - Run 5,000 miles and sample again. A wear rate below 5 ppm per 1,000 miles is a strong result. Between 5 and 10 ppm, repeat the interval with the OEM oil and compare the trend.",
    "Step 4 - If the OEM oil does not reduce the wear rate below 5 ppm per 1,000 miles, test the next viscosity grade for 4,000 to 5,000 miles and sample again.",
    "Step 5 - If viscosity alone does not improve the wear rate, test another oil brand in the best-performing viscosity. Allow a short flush interval before evaluating a full 5,000-mile sample. Oil selection is iterative; viscosity, additive depletion, and wear-rate trends provide the evidence.",
  ];
  y = 129;
  doc.setTextColor(...C.black);
  steps.forEach((step) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.1);
    const lines = doc.splitTextToSize(step, CONTENT_WIDTH - 2);
    doc.text(lines, PAGE.margin + 1, y, { lineHeightFactor: 1.25 });
    y += lines.length * 2.85 + 4;
  });
  doc.setFillColor(...C.gray);
  doc.rect(PAGE.margin, Math.min(y, 202), CONTENT_WIDTH, 5, "F");
}

function drawFooter(doc, pageNumber, pageCount) {
  const y = PAGE.height - 5.2;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.6);
  doc.setTextColor(...C.black);
  doc.text(`Copyright ${new Date().getFullYear()} SPEEDiagnostix`, PAGE.margin, y);
  doc.text("Tech@SPEEDiagnostix.com", PAGE.width / 2, y, { align: "center" });
  doc.text("www.SPEEDiagnostix.com", PAGE.width - PAGE.margin, y, { align: "right" });
  doc.setTextColor(110, 110, 110);
  doc.text(`Page ${pageNumber} of ${pageCount}`, PAGE.width - PAGE.margin, y - 3, { align: "right" });
}

export function buildReportPDF(sample, template, history = [], assets = {}) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "letter" });
  drawFirstPage(doc, sample, template, history, assets);
  drawExplanationPage(doc, sample, assets);
  drawStepsPage(doc, assets);
  drawResourcesPage(doc, assets);
  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    drawFooter(doc, page, pageCount);
  }
  doc.setProperties({
    title: clean(sample?.lab_sample_id) || "SPEEDiagnostix Oil Analysis Report",
    subject: "SPEEDiagnostix oil analysis results and recommendations",
    author: "SPEEDiagnostix",
    creator: "SPEEDiagnostix Dashboard",
  });
  return doc;
}

async function loadImageData(url) {
  if (typeof window === "undefined" || typeof Image === "undefined") return null;
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        canvas.getContext("2d").drawImage(image, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (_) { resolve(null); }
    };
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

export async function generateReportPDF(sample, template, history = []) {
  const [logo, oilGeek] = await Promise.all([
    loadImageData("/images/speediagnostix-email-logo.png"),
    loadImageData("/images/motor-oil-geek.png"),
  ]);
  const doc = buildReportPDF(sample, template, history, { logo, oilGeek });
  const safeId = (sample?.lab_sample_id || "report").replace(/[^a-zA-Z0-9_-]/g, "_");
  doc.save(`SPEEDiagnostix_${safeId}.pdf`);
  return doc;
}
